import type { ChangedImagePrompt, ImagePrompt, ImageRange, ImageResolution } from '@/app/types';
import { calculateRangeAndScale } from '../utils';

interface ImageTooltipContentProps {
  changedImagePrompts: ChangedImagePrompt[];
  activeImageId: string | undefined;
  range: ImageRange;
  domResolution: ImageResolution;
  promptId: string;
  prompt: ImagePrompt;
}

export const ImageTooltipContent = ({
  changedImagePrompts,
  activeImageId,
  range,
  domResolution,
  promptId,
  prompt,
}: ImageTooltipContentProps) => {
  // 优先从缓存中获取最新的裁剪数据
  const cachedData = changedImagePrompts.find((cache) => cache.id === promptId);

  // 构建最终的显示数据
  let finalSelectedRange = prompt.selectedImageRange;

  if (promptId === activeImageId) {
    // 当前正在编辑的图片，使用当前的 range 状态
    if (domResolution.width && domResolution.height && prompt.sourceImageWidth && prompt.sourceImageHeight) {
      const { scale, range: imageRange } = calculateRangeAndScale(
        { width: prompt.sourceImageWidth, height: prompt.sourceImageHeight },
        domResolution,
      );

      const [x1, y1, x2, y2] = range;
      finalSelectedRange = [
        Math.floor((x1 - imageRange[0]) / scale),
        Math.floor((y1 - imageRange[1]) / scale),
        Math.floor((x2 - imageRange[0]) / scale),
        Math.floor((y2 - imageRange[1]) / scale),
      ];
    }
  } else if (cachedData?.range && cachedData?.containerSize && prompt.sourceImageWidth && prompt.sourceImageHeight) {
    // 如果有缓存数据，将缓存的显示坐标转换为原图坐标
    const { scale, range: imageRange } = calculateRangeAndScale(
      { width: prompt.sourceImageWidth, height: prompt.sourceImageHeight },
      cachedData.containerSize,
    );

    const [x1, y1, x2, y2] = cachedData.range;
    finalSelectedRange = [
      Math.floor((x1 - imageRange[0]) / scale),
      Math.floor((y1 - imageRange[1]) / scale),
      Math.floor((x2 - imageRange[0]) / scale),
      Math.floor((y2 - imageRange[1]) / scale),
    ];
  }

  // 如果没有图片尺寸信息或没有选择区域，显示完整图片
  if (!prompt.sourceImageWidth || !prompt.sourceImageHeight || !finalSelectedRange) {
    return (
      <div className="h-48 w-48">
        <img src={prompt.sourceImagePreview ?? ''} alt="image-preview" className="h-full w-full object-contain" />
      </div>
    );
  }

  const [cropX, cropY, cropX2, cropY2] = finalSelectedRange;
  const cropWidth = cropX2 - cropX;
  const cropHeight = cropY2 - cropY;

  // 容器固定尺寸：300px × 300px
  const containerSize = 300;
  const imageRatio = prompt.sourceImageWidth / prompt.sourceImageHeight;

  // 计算图片在容器中的实际显示尺寸（object-contain 效果）
  let actualImageWidth = containerSize;
  let actualImageHeight = containerSize;
  let imageOffsetX = 0;
  let imageOffsetY = 0;

  if (imageRatio > 1) {
    // 横图：以宽度为准，高度缩小并垂直居中
    actualImageHeight = containerSize / imageRatio;
    imageOffsetY = (containerSize - actualImageHeight) / 2;
  } else {
    // 竖图：以高度为准，宽度缩小并水平居中
    actualImageWidth = containerSize * imageRatio;
    imageOffsetX = (containerSize - actualImageWidth) / 2;
  }

  // 计算裁剪区域在原图中的百分比
  const cropXPercent = cropX / prompt.sourceImageWidth;
  const cropYPercent = cropY / prompt.sourceImageHeight;
  const cropWidthPercent = cropWidth / prompt.sourceImageWidth;
  const cropHeightPercent = cropHeight / prompt.sourceImageHeight;

  // 计算裁剪区域在容器中的绝对像素位置
  const absoluteCropX = imageOffsetX + cropXPercent * actualImageWidth;
  const absoluteCropY = imageOffsetY + cropYPercent * actualImageHeight;
  const absoluteCropWidth = cropWidthPercent * actualImageWidth;
  const absoluteCropHeight = cropHeightPercent * actualImageHeight;

  return (
    <div className="relative h-[300px] w-[300px]">
      <img src={prompt.sourceImagePreview ?? ''} alt="image-preview" className="size-full object-contain" />
      <div className="absolute inset-0">
        {/* 上方蒙层 */}
        <div
          className="absolute bg-black bg-opacity-50"
          style={{
            top: 0,
            left: 0,
            right: 0,
            height: `${absoluteCropY}px`,
          }}
        />
        {/* 下方蒙层 */}
        <div
          className="absolute bg-black bg-opacity-50"
          style={{
            top: `${absoluteCropY + absoluteCropHeight}px`,
            left: 0,
            right: 0,
            bottom: 0,
          }}
        />
        {/* 左侧蒙层 */}
        <div
          className="absolute bg-black bg-opacity-50"
          style={{
            top: `${absoluteCropY}px`,
            left: 0,
            width: `${absoluteCropX}px`,
            height: `${absoluteCropHeight}px`,
          }}
        />
        {/* 右侧蒙层 */}
        <div
          className="absolute bg-black bg-opacity-50"
          style={{
            top: `${absoluteCropY}px`,
            left: `${absoluteCropX + absoluteCropWidth}px`,
            right: 0,
            height: `${absoluteCropHeight}px`,
          }}
        />
        {/* 裁剪区域边框 */}
        <div
          className="absolute border border-white border-dashed border-opacity-80"
          style={{
            top: `${absoluteCropY}px`,
            left: `${absoluteCropX}px`,
            width: `${absoluteCropWidth}px`,
            height: `${absoluteCropHeight}px`,
            pointerEvents: 'none',
          }}
        />
      </div>
    </div>
  );
};
